import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { type IRadioBoxProps } from 'shared/ui/Radio'
import { type RowInvite } from 'entities/Users/model/Table/RowInvite'
import { UsersApi } from 'entities/Users'
import { type IParamsCancelInvite, type IResponseCancelInvite } from 'entities/Users/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { CancelInviteModal } from '../CancelInviteModal'

type SeatOption = 'keep' | 'remove'

export class CancelInviteStore {
  private _modalId = nanoid()
  private _seatOption: SeatOption = 'keep'

  constructor(
    readonly entities: IEntitiesStore,
    private _invites: RowInvite[],
    readonly seats_count?: number,
    private _onSuccess?: (response: IResponseCancelInvite) => void | Promise<void>,
    readonly bulk?: {
      all: boolean
      search: string
      total: number
    }
  ) {
    makeAutoObservable(this)
    this.openCancelInviteModal()
  }

  private _onCloseCancelInviteModal = () => {
    modalStore.removeModal(this._modalId)
  }

  onSetSeatOption = (option: SeatOption) => {
    this._seatOption = option
  }

  openCancelInviteModal = () => {
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 480,
      title: 'Cancel invite',
      type: ModalTypeList.DEFAULT,
      ModalContent: CancelInviteModal,
      ModalContentProps: {
        store: this,
      },
      paddingContent: '0 24px 16px 24px',
      onClose: this._onCloseCancelInviteModal,
      primaryAction: {
        text: 'Yes, cancel invite',
        onAction: this._onCancelInvite,
      },
      secondaryAction: {
        text: 'Keep invite',
        onAction: this._onCloseCancelInviteModal,
      },
    })
  }

  private _onCancelInvite = async () => {
    if (!this.invitesCount) return

    try {
      const params: IParamsCancelInvite = this.bulk?.all
        ? {
            all: true,
            search: this.bulk?.search,
          }
        : {
            ids: this._invites.map((item) => item.id),
          }

      const { data } = await UsersApi.cancelInvite({
        ...params,
        remove_seat: this._seatOption === 'remove',
      })

      this._onSuccess?.(data)
      this._onCloseCancelInviteModal()
    } catch (error) {
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Failed to cancel invitation. Please try again',
      })
    }
  }

  get invites() {
    return this._invites
  }

  get seatOption() {
    return this._seatOption
  }

  get invitesCount() {
    if (this.bulk?.all) {
      return this.bulk.total
    }
    return this._invites.length
  }

  get seatOptions(): IRadioBoxProps<SeatOption>[] {
    const seatCount =
      this.seats_count || this._invites.reduce((acc, invite) => acc + (invite.hasSeat ? 1 : 0), 0)
    const suffix = seatCount === 1 ? '' : ` (${seatCount})`

    return [
      {
        id: 'keep',
        value: 'keep',
        text: `Keep seat${suffix}`,
      },
      {
        id: 'remove',
        value: 'remove',
        text: `Remove seat${suffix}`,
      },
    ]
  }

  get isSingleInviteCancel() {
    return this.invitesCount === 1
  }
}
